interface InterfaceMetrics {
  inBytes?: number;
  outBytes?: number;
  inputErrors?: number;
  outputErrors?: number;
  inputDiscards?: number;
  outputDiscards?: number;
}

interface DeviceMetrics {
  cpu_busy?: number;
  mem_used?: number;
}

export interface TraceInterface {
  device_id?: string;
  device_name?: string;
  device_metrics?: DeviceMetrics;
  site?: { id: string; name: string } | null;
  snmp_id?: string;
  interface_name?: string;
  interface_type?: number;
  interface_connectivity_type?: string;
  interface_speed?: number;
  interface_ip_mask?: string;
  interface_ip?: string;
  interface_ip_cidr?: string;
  metrics?: InterfaceMetrics | null;
}

interface TraceHop {
  ttl: number;
  ip: string;
  latency: number; // microseconds
  timeout: boolean;
  trace_end: boolean;
  asn: { id: string; name: string } | null;
  geo: {
    country: { code: string; name: string } | null;
    region: { name: string } | null;
    city: { name: string } | null;
  } | null;
  dns: { hostname: string } | null;
  site: { id: string; name: string } | null;
  ingress_interface?: TraceInterface | null;
  egress_interface?: TraceInterface | null;
}

interface Probe {
  hops: TraceHop[];
  completed: boolean;
}

export interface AlternateIpInfo {
  ip: string;
  hostname: string;
  asnId: string;
  asnName: string;
  country: string;
  city: string;
}

export interface MtrDeviceInfo {
  deviceName: string;
  deviceMetrics?: DeviceMetrics;
  site?: { id: string; name: string } | null;
  ingress?: TraceInterface | null;
  egress?: TraceInterface | null;
}

export interface MtrRow {
  hop: number;
  ip: string;
  hostname: string;
  alternateIps: AlternateIpInfo[];
  pings: (number | null)[]; // per-probe latency in ms, null = timeout
  sent: number;
  recv: number;
  lossPercent: number;
  avgRtt: number; // ms
  maxRtt: number; // ms — maximum RTT across all probes
  jitter: number; // ms — standard deviation of RTT
  asnId: string;
  asnName: string;
  country: string;
  city: string;
  hasDownstreamLoss: boolean;
  device: MtrDeviceInfo | null;
}

export interface MtrSummary {
  roundTripMs: number | null;
  lossPercent: number | null;
  hopCount: number;
  asnCount: number;
  countryCount: number;
  pathMtu: number | null;
}

export function buildMtrRows(probes: Probe[]): MtrRow[] {
  if (!probes.length) {
    return [];
  }

  // Find max TTL across all probes
  let maxTtl = 0;
  for (const probe of probes) {
    for (const hop of probe.hops) {
      if (hop.ttl > maxTtl) {
        maxTtl = hop.ttl;
      }
    }
  }

  const rows: MtrRow[] = [];

  for (let ttl = 1; ttl <= maxTtl; ttl++) {
    // Collect all hops at this TTL across all probes, preserving probe order
    const hopsAtTtl: (TraceHop | null)[] = probes.map((probe) => probe.hops.find((h) => h.ttl === ttl) ?? null);

    const present = hopsAtTtl.filter((h): h is TraceHop => h !== null);
    const sent = present.length;
    if (sent === 0) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const responded = present.filter((h) => !h.timeout);
    const recv = responded.length;

    // Per-probe ping latencies (ms), null when timeout or missing
    const pings: (number | null)[] = hopsAtTtl.map((h) => {
      if (!h || h.timeout) {
        return null;
      }
      return h.latency > 0 ? Math.round((h.latency / 1000) * 100) / 100 : null;
    });

    // Primary IP = most frequent non-empty IP among responding hops
    let primaryIp = '*';
    const alternateIps: AlternateIpInfo[] = [];
    if (responded.length > 0) {
      const ipCounts = new Map<string, number>();
      for (const h of responded) {
        ipCounts.set(h.ip, (ipCounts.get(h.ip) || 0) + 1);
      }
      let maxCount = 0;
      for (const [ip, count] of ipCounts) {
        if (count > maxCount) {
          maxCount = count;
          primaryIp = ip;
        }
      }
      for (const ip of ipCounts.keys()) {
        if (ip !== primaryIp) {
          // Find the first hop with this alternate IP for enrichment
          const altHop = responded.find((h) => h.ip === ip);
          alternateIps.push({
            ip,
            hostname: altHop?.dns?.hostname?.trim() || '-',
            asnId: altHop?.asn?.id || '',
            asnName: altHop?.asn?.name || '',
            country: altHop?.geo?.country?.name || '',
            city: altHop?.geo?.city?.name || ''
          });
        }
      }
    }

    // Collect non-zero latencies (µs)
    const latencies = responded.map((h) => h.latency).filter((l) => l > 0);

    let avgRtt = 0;
    let maxRtt = 0;
    let jitter = 0;

    if (latencies.length > 0) {
      const sum = latencies.reduce((a, b) => a + b, 0);
      avgRtt = sum / latencies.length / 1000;
      maxRtt = Math.max(...latencies) / 1000;

      if (latencies.length > 1) {
        const meanUs = sum / latencies.length;
        const variance = latencies.reduce((acc, l) => acc + (l - meanUs) ** 2, 0) / latencies.length;
        jitter = Math.sqrt(variance) / 1000;
      }
    }

    // Enrichment from the first responding hop
    const enrichmentHop = responded[0] || present[0];
    const hostname = enrichmentHop.dns?.hostname?.trim() || '-';
    const asnId = enrichmentHop.asn?.id || '';
    const asnName = enrichmentHop.asn?.name || '';
    const country = enrichmentHop.geo?.country?.name || '';
    const city = enrichmentHop.geo?.city?.name || '';

    // Device/interface info from the hop data
    let device: MtrDeviceInfo | null = null;
    const ingressIntf = enrichmentHop.ingress_interface;
    const egressIntf = enrichmentHop.egress_interface;
    const deviceIntf = ingressIntf || egressIntf;
    if (deviceIntf?.device_name) {
      device = {
        deviceName: deviceIntf.device_name,
        deviceMetrics: deviceIntf.device_metrics,
        site: deviceIntf.site,
        ingress: ingressIntf,
        egress: egressIntf
      };
    }

    let resolvedHostname = '-';
    if (primaryIp === '*') {
      resolvedHostname = '*';
    } else if (hostname !== '-') {
      resolvedHostname = hostname;
    }

    rows.push({
      hop: ttl,
      ip: primaryIp,
      hostname: resolvedHostname,
      alternateIps,
      pings,
      sent,
      recv,
      lossPercent: Math.round(((sent - recv) / sent) * 1000) / 10,
      avgRtt,
      maxRtt,
      jitter,
      asnId,
      asnName,
      country,
      city,
      hasDownstreamLoss: false, // computed below
      device
    });
  }

  // Compute hasDownstreamLoss: a hop's loss is "real" only if there are NO clean hops (0% loss)
  // between it and the destination. If a clean hop exists after a lossy one, the loss is just
  // ICMP rate limiting — packets got through since the downstream hops responded fine.
  // Last hop (destination) is always real if it has loss.
  for (let i = rows.length - 1; i >= 0; i--) {
    if (i === rows.length - 1) {
      rows[i].hasDownstreamLoss = rows[i].lossPercent > 0;
    } else if (rows[i].lossPercent === 0) {
      // This hop is clean — break the chain
      rows[i].hasDownstreamLoss = false;
    } else {
      // This hop has loss — only flag it if ALL subsequent hops also have loss (no clean gap)
      rows[i].hasDownstreamLoss = rows[i + 1].lossPercent > 0 && rows[i + 1].hasDownstreamLoss;
    }
  }

  return rows;
}

export function buildMtrSummary(rows: MtrRow[], mtu: number | null): MtrSummary {
  if (!rows.length) {
    return { roundTripMs: null, lossPercent: null, hopCount: 0, asnCount: 0, countryCount: 0, pathMtu: mtu };
  }

  const targetRow = rows[rows.length - 1];

  const uniqueAsns = new Set(rows.map((r) => r.asnId).filter(Boolean));
  const uniqueCountries = new Set(rows.map((r) => r.country).filter(Boolean));

  return {
    roundTripMs: targetRow.avgRtt || null,
    lossPercent: targetRow.lossPercent,
    hopCount: rows.length,
    asnCount: uniqueAsns.size,
    countryCount: uniqueCountries.size,
    pathMtu: mtu
  };
}
